import React from "react";
import {
  FileText,
  Calendar,
  Video,
  MessageCircle,
  Upload,
  TrendingUp,
  BookOpen,
  Activity,
  Link2,
  ArrowUpRight,
} from "lucide-react";
import { INTAKE_URL } from "@/constants/links";

const PORTAL_CAPABILITIES = [
  { icon: FileText, title: "Complete intake forms & follow-up questionnaires" },
  { icon: Calendar, title: "Schedule and manage appointments" },
  { icon: Video, title: "Access telehealth visits when appropriate" },
  { icon: MessageCircle, title: "Communicate securely with the care team" },
  { icon: Upload, title: "Upload requested information or documents" },
  { icon: TrendingUp, title: "Track goals and progress" },
  { icon: BookOpen, title: "Review educational resources" },
  { icon: Activity, title: "Submit wellness updates" },
  { icon: Link2, title: "Stay connected between visits" },
];

export default function UnifiedModelSection() {
  return (
    <section
      id="unified-model"
      className="relative border-b border-[color:var(--iw-border)] bg-[color:var(--iw-bg)] py-20 sm:py-28"
    >
      <div className="absolute inset-0 iw-grid-bg opacity-40" aria-hidden />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        {/* ── Section header ────────────────────────────── */}
        <div className="mb-14 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-7">
            <div className="iw-overline mb-3">§ Portal & App</div>
            <h2 className="font-display text-3xl font-semibold leading-[1.05] tracking-tight text-[color:var(--iw-primary)] sm:text-4xl lg:text-5xl xl:text-[3.2rem]">
              The Integrated Wellness{" "}
              <span className="text-[color:var(--iw-blue)]">Portal & App</span>.{" "}
              <span className="text-[color:var(--iw-accent-red)]">One connected experience</span>.
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-lg leading-relaxed text-[color:var(--iw-body)] sm:text-xl">
              A simple, connected way to access your care, complete forms, communicate with the team, manage appointments, and stay engaged throughout your wellness journey. Built to support a more proactive, organized, and personalized experience — so our medical team can understand your goals, lifestyle, progress, and needs over time.
            </p>
          </div>
        </div>

        {/* ── What You Can Do ────────────────────────────── */}
        <div className="mb-16">
          <div className="mb-8 flex items-end justify-between gap-6">
            <div>
              <div className="iw-overline mb-2">§ What you can do</div>
              <h3 className="font-display text-2xl font-semibold tracking-tight text-[color:var(--iw-primary)] sm:text-3xl lg:text-4xl">
                Everything, in one convenient place.
              </h3>
            </div>
          </div>

          <ul className="grid grid-cols-1 gap-px overflow-hidden border border-[color:var(--iw-border)] bg-[color:var(--iw-border)] sm:grid-cols-2 lg:grid-cols-3">
            {PORTAL_CAPABILITIES.map((c) => (
              <li
                key={c.title}
                className="iw-scan-sweep group flex items-start gap-4 bg-white p-6 transition-colors hover:bg-[color:var(--iw-bg)] sm:p-7"
              >
                <span className="inline-grid h-12 w-12 flex-shrink-0 place-items-center border border-[color:var(--iw-blue)]/60 text-[color:var(--iw-blue)] transition-colors group-hover:border-[color:var(--iw-blue)] group-hover:bg-[color:var(--iw-blue)]/5">
                  <c.icon className="h-5 w-5" />
                </span>
                <div className="font-display text-lg font-semibold leading-snug tracking-tight text-[color:var(--iw-primary)] sm:text-xl">
                  {c.title}
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* ── Closing "Smarter way to stay connected" band ─ */}
        <div className="flex flex-col items-start justify-between gap-6 border border-[color:var(--iw-border)] bg-[color:var(--iw-primary)] p-8 text-white sm:p-12 lg:flex-row lg:items-center">
          <div className="max-w-3xl">
            <div className="font-mono text-[12px] uppercase tracking-[0.24em] text-[color:var(--iw-accent-red)] sm:text-[13px]">
              A smarter way to stay connected
            </div>
            <h3 className="mt-3 font-display text-2xl font-semibold leading-tight tracking-tight sm:text-3xl lg:text-4xl">
              Secure communication, digital intake, telehealth, progress tracking, education, and ongoing support —{" "}
              <span className="text-[color:var(--iw-accent-red)]">all in one modern experience</span>.
            </h3>
            <p className="mt-4 text-base leading-relaxed text-white/95 sm:text-lg">
              Designed for patients who want a more connected, proactive, and personalized approach to health optimization.
            </p>
          </div>
          <a
            href={INTAKE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="iw-btn-red inline-flex flex-shrink-0 items-center gap-2 px-7 py-4 font-mono text-[12px] uppercase tracking-[0.22em] sm:text-[13px]"
          >
            Get Started <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>

        {/* ── Book showcase — "The Unified Medical Model" ─ */}
        <div className="mt-14 grid grid-cols-1 items-center gap-8 border border-[color:var(--iw-border)] bg-[color:var(--iw-primary)] p-7 text-white sm:p-10 lg:grid-cols-12 lg:gap-12">
          <div className="flex justify-center lg:col-span-4 lg:justify-start">
            <a
              href="https://www.amazon.com/dp/B0GMYV4ZF1"
              target="_blank"
              rel="noopener noreferrer"
              className="block transition-transform duration-300 hover:-translate-y-1"
            >
              <img
                src="https://m.media-amazon.com/images/I/81-kkdhVgLL._SY342_.jpg"
                srcSet="https://m.media-amazon.com/images/I/81-kkdhVgLL._SY342_.jpg 1x, https://m.media-amazon.com/images/I/81-kkdhVgLL._SY679_.jpg 2x"
                alt="The Unified Medical Model — book by John Campetella"
                className="w-56 border border-white/10 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.6)] sm:w-64 lg:w-full lg:max-w-xs"
                loading="lazy"
                decoding="async"
                referrerPolicy="no-referrer"
              />
            </a>
          </div>
          <div className="lg:col-span-8">
            <div className="font-mono text-[12px] uppercase tracking-[0.22em] text-[color:var(--iw-accent-red)] sm:text-[13px]">
              Read the book
            </div>
            <h3 className="mt-2 font-display text-2xl font-semibold leading-tight tracking-tight sm:text-3xl lg:text-4xl">
              The Unified Medical Model
            </h3>
            <p className="mt-2 font-mono text-[13px] uppercase tracking-[0.22em] text-[color:var(--iw-blue-bright)] sm:text-[14px]">
              by John Campetella
            </p>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-white/95 sm:text-lg">
              How systems-based medicine and augmented intelligence are transforming healthcare — the framework behind the ecosystem you see here.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
